import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Container,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import LockIcon from '@mui/icons-material/Lock';
import FileDropZone from '../components/common/FileDropZone';
import {
  uploadSessionsService,
  PublicSessionInfo,
  UploadSessionFileInfo,
} from '../services/uploadSessionsService';

type PageState = 'loading' | 'not_found' | 'expired' | 'auth' | 'upload';

const PublicUploadPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const theme = useTheme();
  const accent = theme.palette.primary.main;

  const [pageState, setPageState] = useState<PageState>('loading');
  const [sessionInfo, setSessionInfo] = useState<PublicSessionInfo | null>(null);
  const [secret, setSecret] = useState('');
  const [secretError, setSecretError] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadedFiles, setUploadedFiles] = useState<UploadSessionFileInfo[]>([]);
  const [uploadMessage, setUploadMessage] = useState<{ text: string; severity: 'success' | 'error' } | null>(null);

  useEffect(() => {
    if (!token) {
      setPageState('not_found');
      return;
    }
    (async () => {
      try {
        const info = await uploadSessionsService.getPublicSession(token);
        setSessionInfo(info);
        if (info.status === 'abgelaufen') {
          setPageState('expired');
        } else {
          setPageState('auth');
        }
      } catch {
        setPageState('not_found');
      }
    })();
  }, [token]);

  const handleVerify = async () => {
    if (!token || !secret.trim()) return;
    try {
      setVerifying(true);
      setSecretError('');
      const valid = await uploadSessionsService.verifySecret(token, secret.trim());
      if (valid) {
        setPageState('upload');
        loadFiles();
      } else {
        setSecretError('Ungültiges Passwort');
      }
    } catch (err: any) {
      if (err?.response?.status === 410) {
        setPageState('expired');
      } else {
        setSecretError('Fehler bei der Verifizierung');
      }
    } finally {
      setVerifying(false);
    }
  };

  const loadFiles = useCallback(async () => {
    if (!token) return;
    try {
      const files = await uploadSessionsService.listPublicFiles(token, secret.trim());
      setUploadedFiles(files);
    } catch {
      // silently fail
    }
  }, [token, secret]);

  const handleFilesDropped = async (files: File[]) => {
    if (!token) return;
    try {
      setUploading(true);
      setUploadMessage(null);
      setUploadProgress(0);
      await uploadSessionsService.uploadFiles(token, secret.trim(), files, setUploadProgress);
      setUploadMessage({
        text: `${files.length} Datei${files.length > 1 ? 'en' : ''} erfolgreich hochgeladen`,
        severity: 'success',
      });
      loadFiles();
    } catch (err: any) {
      const detail = err?.response?.data?.detail || 'Upload fehlgeschlagen';
      setUploadMessage({ text: detail, severity: 'error' });
      loadFiles();
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const formatFileSize = (bytes: number | null) => {
    if (bytes == null) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
  };

  const renderContent = () => {
    switch (pageState) {
      case 'loading':
        return (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        );

      case 'not_found':
        return (
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 6 }}>
              <ErrorOutlineIcon sx={{ fontSize: 64, color: 'error.main', mb: 2 }} />
              <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
                Session nicht gefunden
              </Typography>
              <Typography color="text.secondary">
                Der angegebene Upload-Link ist ungültig oder existiert nicht mehr.
              </Typography>
            </CardContent>
          </Card>
        );

      case 'expired':
        return (
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 6 }}>
              <ErrorOutlineIcon sx={{ fontSize: 64, color: 'warning.main', mb: 2 }} />
              <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
                Session abgelaufen
              </Typography>
              <Typography color="text.secondary">
                Diese Upload-Session ist nicht mehr gültig. Bitte kontaktieren Sie Ihren Ansprechpartner.
              </Typography>
            </CardContent>
          </Card>
        );

      case 'auth':
        return (
          <Card>
            <CardContent sx={{ py: 4, px: { xs: 3, sm: 5 } }}>
              <Box sx={{ textAlign: 'center', mb: 3 }}>
                <LockIcon sx={{ fontSize: 48, color: accent, mb: 1 }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Zugang bestätigen
                </Typography>
                {sessionInfo && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    {sessionInfo.beschreibung}
                  </Typography>
                )}
              </Box>
              <TextField
                label="Passwort"
                type="password"
                value={secret}
                onChange={(e) => {
                  setSecret(e.target.value);
                  setSecretError('');
                }}
                onKeyDown={(e) => e.key === 'Enter' && handleVerify()}
                fullWidth
                error={!!secretError}
                helperText={secretError}
                sx={{ mb: 2 }}
              />
              <Button
                variant="contained"
                fullWidth
                onClick={handleVerify}
                disabled={verifying || !secret.trim()}
                sx={{ py: 1.2 }}
              >
                {verifying ? <CircularProgress size={22} color="inherit" /> : 'Zugang bestätigen'}
              </Button>
            </CardContent>
          </Card>
        );

      case 'upload':
        return (
          <>
            {sessionInfo && (
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {sessionInfo.beschreibung}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Gültig bis {new Date(sessionInfo.gueltig_bis).toLocaleDateString('de-DE')}
                  </Typography>
                </CardContent>
              </Card>
            )}

            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                  Dateien hochladen
                </Typography>
                <FileDropZone onFilesDropped={handleFilesDropped} uploading={uploading} />
                {uploading && uploadProgress > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <LinearProgress variant="determinate" value={uploadProgress} />
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                      {uploadProgress}%
                    </Typography>
                  </Box>
                )}
                {uploadMessage && (
                  <Alert severity={uploadMessage.severity} sx={{ mt: 2 }}>
                    {uploadMessage.text}
                  </Alert>
                )}
              </CardContent>
            </Card>

            {uploadedFiles.length > 0 && (
              <Card>
                <CardContent>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                    Hochgeladene Dateien ({uploadedFiles.length})
                  </Typography>
                  <List dense>
                    {uploadedFiles.map((f) => (
                      <ListItem key={f.id}>
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <InsertDriveFileIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText
                          primary={f.dateiname}
                          secondary={formatFileSize(f.dateigroesse)}
                        />
                        <CheckCircleIcon fontSize="small" color="success" />
                      </ListItem>
                    ))}
                  </List>
                </CardContent>
              </Card>
            )}
          </>
        );
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: theme.palette.background.default,
        py: { xs: 3, sm: 6 },
      }}
    >
      <Container maxWidth="sm">
        {/* Header with logo */}
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Box
            component="img"
            src="/stark_logo.png"
            alt="STARK IT-Security"
            sx={{ height: 48, mb: 2, objectFit: 'contain' }}
            onError={(e: any) => { e.target.style.display = 'none'; }}
          />
          <Typography
            variant="h5"
            sx={{ fontWeight: 700, color: accent }}
          >
            Sicherer Datei-Upload
          </Typography>
        </Box>

        {renderContent()}

        {/* Footer */}
        <Box sx={{ textAlign: 'center', mt: 4 }}>
          <Typography variant="caption" color="text.secondary">
            STARK IT-Security GmbH &mdash; Sicherer Dateitransfer
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default PublicUploadPage;
